import React, { useState, useEffect } from 'react';
import '../../../App.css';
import { useNavigate } from 'react-router-dom';
import { bookingService, talentService, userService } from '../../../api/apiService';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    // 'confirmedcreditnote' is awaiting its Gutschrift — for the talent simply a confirmed job.
    case 'confirmed':
    case 'confirmedcreditnote':
    case 'booked':
      return '#15a326';
    case 'new order':
      return '#ffffff';
    case 'requested':
      return '#FF8806';
    case 'talentaccepted':
      return '#3de651';
    case 'declined request':
      return '#ff3c00';
    case 'optioned':
      return '#0060ff';
    case 'hold':
    case 'talent on hold':
    case 'talenton hold':
      return '#800080';
    case 'optionsolved':
      return '#FFFF00';
    case 'lano':
      return '#116c11';
    default:
      return '#606060';
  }
};

const TalentOrdersList = () => {
  const navigate = useNavigate();
  const [talentIds, setTalentIds] = useState([]);
  const [talentData, setTalentData] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [groupedBookings, setGroupedBookings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const toggleExpand = (bookingId) => {
    setExpandedItems(prev => ({
      ...prev,
      [bookingId]: !prev[bookingId]
    }));
  };

  // Ensures talentId is a string
  const ensureString = (id) => id ? id.toString() : '';

  useEffect(() => {
    const fetchUserTalents = async () => {
      try {
        setLoading(true);
        
        // Get user profile
        const userProfileResponse = await userService.getProfile();
        const userData = userProfileResponse?.data?.user;
        
        if (!userData) {
          console.error("User data not found");
          setError("Failed to load user data");
          setLoading(false);
          return;
        }
        
        // console.log("User profile data:", userData);
        
        // Extract talent IDs from user profile
        let fetchedTalentIds = [];
        if (userData.talents && Array.isArray(userData.talents)) {
          fetchedTalentIds = userData.talents.map(talent => ensureString(talent.db_id));
        }
        
        setTalentIds(fetchedTalentIds);
        
        // Fetch talent details for each ID
        const talentsData = [];
        for (const id of fetchedTalentIds) {
          try {
            // console.log(`Fetching talent details for ID: ${id}`);
            const talentResponse = await talentService.getTalentById(id);
            // console.log(`Talent response for ${id}:`, JSON.stringify(talentResponse, null, 2));
            
            if (talentResponse?.data) {
              // Log the specific data structure to see which fields are available
              // console.log(`Talent data structure for ${id}:`, {
                // id: id,
                // data: talentResponse.data,
                // name: talentResponse.data.name,
                // email: talentResponse.data.email,
                // function: talentResponse.data.function,
                // Funktion: talentResponse.data.Funktion,
                // job: talentResponse.data.job,
                // position: talentResponse.data.position,
                // Name: talentResponse.data.Name
              // });
              
              // Try to use name field from different possible properties
              const talentName = talentResponse.data.name || talentResponse.data.Name || 'Unknown Name';
              const talentFunction = talentResponse.data.function || talentResponse.data.Funktion || 
                                     talentResponse.data.job || talentResponse.data.position || 'No Function';
              
              const talentItem = {
                id,
                name: talentName,
                email: talentResponse.data.email,
                rate: talentResponse.data.rate,
                lanoid: talentResponse.data.lanoId,
                Funktion: talentFunction
              };
              // console.log(`Added talent to list:`, talentItem);
              talentsData.push(talentItem);
            } else {
              // console.warn(`No data found for talent ${id}`);
            }
          } catch (err) {
            console.error(`Error fetching talent ${id}:`, err);
          }
        }
        
        // console.log('Unsorted talents data:', talentsData);
        
        // Sort talents by name with null checks
        const sortedTalents = talentsData.sort((a, b) => {
          const nameA = a.name || 'Unknown';
          const nameB = b.name || 'Unknown';
          return nameA.localeCompare(nameB);
        });
        setTalentData(sortedTalents);
        
        // Fetch bookings directly here instead of calling fetchBookingsForTalents
        try {
          const allBookings = [];
          
          // Calculate date from 3 days ago
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          const fromDateStr = threeDaysAgo.toISOString().split('T')[0]; // Format as YYYY-MM-DD
          
          // console.log(`Fetching bookings from ${fromDateStr} onwards (3 days ago and future)`);
          
          // Fetch bookings for each talent using the endpoint that gets ALL bookings
          for (const id of fetchedTalentIds) {
            try {
              // console.log(`Fetching all bookings for talent ${id}`);
              const response = await bookingService.getBookingsByTalentId(id);
              if (response?.data?.bookings) {
                const talentBookings = response.data.bookings;
                // console.log(`Fetched ${talentBookings.length} total bookings for talent ${id}`);
                
                // Filter bookings to only include those from 3 days ago and future
                const filteredBookings = talentBookings.filter(booking => {
                  if (!booking.start_date) return false;
                  const bookingDate = new Date(booking.start_date);
                  return bookingDate >= threeDaysAgo;
                });
                
                // console.log(`Filtered to ${filteredBookings.length} bookings (3 days ago and future) for talent ${id}`);
                allBookings.push(...filteredBookings);
              } else if (Array.isArray(response?.data)) {
                // Handle case where bookings are directly in response.data
                const talentBookings = response.data;
                // console.log(`Fetched ${talentBookings.length} total bookings for talent ${id}`);
                
                // Filter bookings to only include those from 3 days ago and future
                const filteredBookings = talentBookings.filter(booking => {
                  if (!booking.start_date) return false;
                  const bookingDate = new Date(booking.start_date);
                  return bookingDate >= threeDaysAgo;
                });
                
                // console.log(`Filtered to ${filteredBookings.length} bookings (3 days ago and future) for talent ${id}`);
                allBookings.push(...filteredBookings);
              }
            } catch (err) {
              console.error(`Error fetching bookings for talent ${id}:`, err);
            }
          }
          
          // console.log(`Total filtered bookings loaded: ${allBookings.length}`);
          setBookings(allBookings);
          setGroupedBookings(groupBookings(allBookings));
        } catch (err) {
          console.error("Error fetching bookings:", err);
          setError("Failed to load booking information");
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching user talents:", err);
        setError("Failed to load talent information");
        setLoading(false);
      }
    };

    fetchUserTalents();
  }, []);

  const groupBookings = (bookingsList) => {
    const grouped = {};

    // Group bookings by their group ID
    bookingsList.forEach((booking) => {
      const groupId = booking.group || 'ungrouped';
      if (!grouped[groupId]) {
        grouped[groupId] = [];
      }
      grouped[groupId].push(booking);
    });

    // Sort bookings in each group and mark the last one
    Object.keys(grouped).forEach((groupId) => {
      const sortedBookings = grouped[groupId].sort((a, b) => 
        new Date(a.start_date) - new Date(b.start_date)
      );
      sortedBookings[sortedBookings.length - 1].isLast = true; // Mark the last booking
    });

    return grouped;
  };

  const handleBookingStatusChange = async (booking, bookingId, talent, option, action) => {
    try {
      let status;
      if (action === 'accept') {
        if (booking.isdummy) {
          status = 'talentOnHold';
        } else {   
          status = option === 'option' ? 'optioned' : 'confirmed';
        }
      } else if (action === 'reject') {
        status = 'declined request';
        
        // Create a new booking with the same details but no talent assigned
        const newBookingData = {
          ...booking,
          status: 'new order',
          talent_id: '',
          group: null,
          groupColor: ''
        };
        
        // Remove ID from new booking data
        delete newBookingData.id;
        
        // Create the new booking
        await bookingService.createBooking(newBookingData);
      }

      // Update the existing booking status
      await bookingService.updateBookingStatus(bookingId, status);

      // Update local state
      const updatedBookings = bookings.map(b => 
        b.id === bookingId ? { ...b, status: status } : b
      );
      setBookings(updatedBookings);
      setGroupedBookings(groupBookings(updatedBookings));

      alert('Updated successfully!');
      // Refresh the bookings data
      refreshBookingsData();
    } catch (error) {
      console.error("Error updating booking status:", error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Add a separate function to refresh booking data
  const refreshBookingsData = async () => {
    try {
      const allBookings = [];
      
      // Calculate date from 3 days ago
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      const fromDateStr = threeDaysAgo.toISOString().split('T')[0]; // Format as YYYY-MM-DD
      
      // console.log(`Refreshing bookings from ${fromDateStr} onwards (3 days ago and future)`);
      
      // Fetch bookings for each talent using the endpoint that gets ALL bookings
      for (const id of talentIds) {
        try {
          // console.log(`Refreshing all bookings for talent ${id}`);
          const response = await bookingService.getBookingsByTalentId(id);
          if (response?.data?.bookings) {
            const talentBookings = response.data.bookings;
            // console.log(`Fetched ${talentBookings.length} total bookings for talent ${id}`);
            
            // Filter bookings to only include those from 3 days ago and future
            const filteredBookings = talentBookings.filter(booking => {
              if (!booking.start_date) return false;
              const bookingDate = new Date(booking.start_date);
              return bookingDate >= threeDaysAgo;
            });
            
            // console.log(`Filtered to ${filteredBookings.length} bookings (3 days ago and future) for talent ${id}`);
            allBookings.push(...filteredBookings);
          } else if (Array.isArray(response?.data)) {
            // Handle case where bookings are directly in response.data
            const talentBookings = response.data;
            // console.log(`Fetched ${talentBookings.length} total bookings for talent ${id}`);
            
            // Filter bookings to only include those from 3 days ago and future
            const filteredBookings = talentBookings.filter(booking => {
              if (!booking.start_date) return false;
              const bookingDate = new Date(booking.start_date);
              return bookingDate >= threeDaysAgo;
            });
            
            // console.log(`Filtered to ${filteredBookings.length} bookings (3 days ago and future) for talent ${id}`);
            allBookings.push(...filteredBookings);
          }
        } catch (err) {
          console.error(`Error fetching bookings for talent ${id}:`, err);
        }
      }
      
      // console.log(`Total filtered bookings refreshed: ${allBookings.length}`);
      setBookings(allBookings);
      setGroupedBookings(groupBookings(allBookings));
    } catch (err) {
      console.error("Error refreshing bookings:", err);
      setError("Failed to refresh booking information");
    }
  };

  // Helper function to format date from string or timestamp
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", dateStr, error);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <div className="loading">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <div className='h1-container' style={{ justifyContent: 'space-between', display: 'flex' }}>
            <h1>Projects</h1>
            <button className='big-button' onClick={() => navigate('../my-calendar')}>Calendar</button>
          </div>
        </div>
        <div>
          {talentData.map(talent => (
            <div key={talent.id}>
              <div className="content-wrapper">
                <h2 style={{ marginTop:'70px', marginBottom: '50px' }}>{talent.name} - {talent.Funktion}</h2>
              </div>
              <div className='content-wrapper content-wrapper-talent-list'>
                <div className='list-tasks-header desktop-only'>
                  <div style={{ flexBasis: '150px' }}>Client</div>
                  <div style={{ flexBasis: '150px' }}>Status</div>
                  <div style={{ flexBasis: '150px' }}>Date</div>
                  <div style={{ flexBasis: '150px' }}>Start-End</div>
                  <div style={{ flexBasis: '150px' }}>KPI</div>
                  <div style={{ flexBasis: '150px' }}>Set</div>
                  <div style={{ flexBasis: '150px' }}>Rate</div>
                </div>
                {Object.entries(groupedBookings).length === 0 ? (
                  <div className="no-bookings-message" style={{ padding: '20px', textAlign: 'center' }}>
                    No bookings found for this talent.
                  </div>
                ) : (
                  Object.entries(groupedBookings).map(([groupId, groupedBooking]) => {
                    // Filter bookings for the current talent only
                    const bookingsForTalent = groupedBooking.filter(booking => 
                      booking.talent_id === talent.id || 
                      booking.talentId === talent.id
                    );
                    
                    if (bookingsForTalent.length === 0) {
                      return null; // Skip this group if there are no bookings for this talent
                    }
                    
                    return bookingsForTalent.map((booking) => (
                      <div 
                        className='tlist-mobile-container'
                        key={booking.id}
                      >
                        <div 
                          className='tlist-preview-row'
                          onClick={() => toggleExpand(booking.id)}
                          style={{ 
                            borderLeft: `4px solid ${getStatusColor(booking.status)}`,
                            backgroundColor: booking.status === 'requested' ? '#fff8ec' : '#fff'
                          }}
                        >
                          <div className='tlist-preview-date'>
                            {formatDate(booking.start_date)}
                          </div>
                          <div className='tlist-preview-client'>
                            {booking.client?.label || 'No Client'}
                          </div>
                          <div className='tlist-preview-arrow'>
                            {expandedItems[booking.id] ? '↑' : '↓'}
                          </div>
                        </div>

                        {expandedItems[booking.id] && (
                          <div className='tlist-expanded-content'>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Status</div>
                              <div>{booking.status}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Start-End</div>
                              <div>{booking.start_time || '-'} - {booking.end_time || '-'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>KPI</div>
                              <div>{booking.kpi || '-'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Set</div>
                              <div>{booking.set || '-'}</div>
                            </div>
                            <div className='tlist-detail-row'>
                              <div className='tlist-detail-heading'>Rate</div>
                              <div>{talent.rate || '-'}</div>
                            </div>

                            {booking.status === 'requested' && (
                              <div className='tlist-actions'>
                                {booking.option === 'option'
                                  ? <button className='button' onClick={() => handleBookingStatusChange(booking, booking.id, talent, booking.option, 'accept')}>Accept First Option</button>
                                  : <button className='button' onClick={() => handleBookingStatusChange(booking, booking.id, talent, booking.option, 'accept')}>Accept Confirmation</button>
                                }
                                <button className='button' onClick={() => handleBookingStatusChange(booking, booking.id, talent, booking.option, 'reject')}>Deny</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ));
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TalentOrdersList;
  